import { useState } from 'react';

/**
 * PositionSizer — pure client-side position sizing calculator.
 *
 * No backend required. Helps traders calculate correct position size
 * based on account risk % and stop loss distance.
 *
 * Available at /tools/position-sizer (all tiers)
 */

export default function PositionSizer() {
  const [form, setForm] = useState({
    accountSize: '50000',
    riskPercent: '1',
    entryPrice: '150',
    stopLoss: '145',
    targetPrice: '',
    isOptions: false,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Calculations ──────────────────────────────────────────────────────────
  const accountSize = parseFloat(form.accountSize) || 0;
  const riskPercent = parseFloat(form.riskPercent) || 0;
  const entryPrice = parseFloat(form.entryPrice) || 0;
  const stopLoss = parseFloat(form.stopLoss) || 0;
  const targetPrice = parseFloat(form.targetPrice) || 0;

  const riskAmount = accountSize * (riskPercent / 100);
  const riskPerShare = Math.abs(entryPrice - stopLoss);
  const shares = riskPerShare > 0 ? Math.floor(riskAmount / riskPerShare) : 0;
  const contracts = form.isOptions ? Math.floor(shares / 100) : 0;
  const totalCost = form.isOptions
    ? contracts * entryPrice * 100
    : shares * entryPrice;
  const rr =
    targetPrice && entryPrice && stopLoss && riskPerShare > 0
      ? Math.abs(targetPrice - entryPrice) / riskPerShare
      : null;
  const percentOfAccount = accountSize > 0 ? (totalCost / accountSize) * 100 : 0;

  const isLong = entryPrice > stopLoss;
  const tradeDirection = isLong ? 'Long' : 'Short';

  // ── Helpers ───────────────────────────────────────────────────────────────
  const Row = ({ label, value, color = 'text-white', small = false }) => (
    <div className="flex justify-between items-baseline">
      <span className={`${small ? 'text-xs' : 'text-sm'} text-gray-400`}>{label}</span>
      <span className={`${small ? 'text-sm' : 'text-base'} font-semibold ${color}`}>{value}</span>
    </div>
  );

  const Input = ({ label, id, value, onChange, suffix = '', prefix = '', type = 'number', min, step, note }) => (
    <div>
      <label htmlFor={id} className="text-sm text-gray-400 block mb-1">
        {label}
        {note && <span className="text-gray-600 text-xs ml-1">({note})</span>}
      </label>
      <div className="relative flex items-center">
        {prefix && (
          <span className="absolute left-3 text-gray-400 text-sm select-none">{prefix}</span>
        )}
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          min={min}
          step={step}
          className={`w-full bg-gray-800 border border-gray-700 rounded-lg py-2 text-white text-sm focus:border-blue-500 focus:outline-none ${prefix ? 'pl-7' : 'px-3'} ${suffix ? 'pr-10' : 'pr-3'}`}
        />
        {suffix && (
          <span className="absolute right-3 text-gray-400 text-sm select-none">{suffix}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Position Sizing Calculator</h1>
          <p className="text-gray-400 text-sm mt-1">
            Calculate correct position size based on account risk % and stop loss distance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ── Inputs ── */}
          <div className="bg-gray-900 border border-white/10 rounded-xl p-5 space-y-4">
            <h2 className="font-semibold text-gray-200 text-sm uppercase tracking-wider">Inputs</h2>

            <Input
              label="Account Size"
              id="accountSize"
              value={form.accountSize}
              onChange={v => set('accountSize', v)}
              prefix="$"
              min="0"
              step="1000"
            />
            <Input
              label="Risk Per Trade"
              id="riskPercent"
              value={form.riskPercent}
              onChange={v => set('riskPercent', v)}
              suffix="%"
              min="0.1"
              step="0.1"
              note="recommended: 0.5–2%"
            />
            <Input
              label="Entry Price"
              id="entryPrice"
              value={form.entryPrice}
              onChange={v => set('entryPrice', v)}
              prefix="$"
              min="0"
              step="0.01"
            />
            <Input
              label="Stop Loss"
              id="stopLoss"
              value={form.stopLoss}
              onChange={v => set('stopLoss', v)}
              prefix="$"
              min="0"
              step="0.01"
            />
            <Input
              label="Target Price"
              id="targetPrice"
              value={form.targetPrice}
              onChange={v => set('targetPrice', v)}
              prefix="$"
              min="0"
              step="0.01"
              note="optional — for R:R calc"
            />

            {/* Options toggle */}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                onClick={() => set('isOptions', !form.isOptions)}
                className={`relative w-10 h-5 rounded-full transition-colors ${form.isOptions ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isOptions ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm text-gray-300">Options contract sizing (÷100)</span>
            </label>
          </div>

          {/* ── Results ── */}
          <div className="bg-gray-900 border border-white/10 rounded-xl p-5 space-y-4">
            <h2 className="font-semibold text-gray-200 text-sm uppercase tracking-wider">Results</h2>

            <div className={`rounded-lg px-4 py-3 ${isLong ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
              <p className={`text-xs font-bold uppercase tracking-wider ${isLong ? 'text-green-400' : 'text-red-400'}`}>
                {tradeDirection} Trade
              </p>
            </div>

            <div className="space-y-3">
              <Row
                label="Risk Amount"
                value={riskAmount > 0 ? `$${riskAmount.toFixed(2)}` : '—'}
                color="text-amber-400"
              />
              <Row
                label="Risk Per Share"
                value={riskPerShare > 0 ? `$${riskPerShare.toFixed(2)}` : '—'}
              />
              <div className="border-t border-white/10 pt-3">
                <Row
                  label={form.isOptions ? 'Contracts' : 'Shares'}
                  value={form.isOptions ? (contracts > 0 ? contracts : '—') : (shares > 0 ? shares : '—')}
                  color={form.isOptions ? 'text-blue-400' : 'text-emerald-400'}
                />
                {form.isOptions && (
                  <Row
                    label="Equivalent Shares"
                    value={shares > 0 ? shares : '—'}
                    small
                  />
                )}
              </div>
              <div className="border-t border-white/10 pt-3">
                <Row
                  label="Total Cost"
                  value={totalCost > 0 ? `$${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                />
                <Row
                  label="% of Account"
                  value={percentOfAccount > 0 ? `${percentOfAccount.toFixed(1)}%` : '—'}
                  color={percentOfAccount > 25 ? 'text-amber-400' : 'text-gray-300'}
                  small
                />
              </div>
              {rr !== null && (
                <div className="border-t border-white/10 pt-3">
                  <Row
                    label="Risk:Reward"
                    value={`1 : ${rr.toFixed(2)}`}
                    color={rr >= 2 ? 'text-green-400' : rr >= 1 ? 'text-amber-400' : 'text-red-400'}
                  />
                  <Row
                    label="Potential Profit"
                    value={`$${(rr * riskAmount).toFixed(2)}`}
                    color="text-green-400"
                    small
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Example ── */}
        <div className="mt-6 bg-gray-900/50 border border-white/5 rounded-xl p-4">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">How it works</p>
          <p className="text-xs text-gray-400">
            <span className="text-white">Risk Amount</span> = Account × Risk% &nbsp;·&nbsp;
            <span className="text-white">Risk/Share</span> = |Entry − Stop| &nbsp;·&nbsp;
            <span className="text-white">Shares</span> = Risk Amount ÷ Risk/Share &nbsp;·&nbsp;
            <span className="text-white">R:R</span> = |Target − Entry| ÷ |Entry − Stop|
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Example: $50k account, 1% risk ($500), $150 entry, $145 stop ($5/share) → <span className="text-white">100 shares</span>, $15k capital, 3:1 R:R if target $165.
          </p>
        </div>
      </div>
    </div>
  );
}
